/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import ko from "knockout";
import birdata from "../api.js";
import util from "../util/util.js";
import h from "util/dom-builder.js";
import '../ui/quiz.js';
import googleMaps from '../util/google-maps.js';
import markerLayer from "../map/marker-layer.js";
import UTMGridMapType from "../map/utm-grid-layer.js";
import * as utmGrid from "../map/utm-grid.js";
import * as eventUtil from '../util/event-util.js';

import programs from '../programs.js';

import "../util/knockout-pikaday.js";

var BookSiteViewModel = function(params) {
	var self = this;

	var existingBookingCode = util.queryParams().code;
	var eventCode = util.queryParams().event;
	var bookingDate = util.queryParams().date;
	var siteID = util.queryParams().site;
	var today = util.formatISODate(new Date());

	self.siteName = ko.observable();
	self.loaded = ko.observable(false);
	self.error = ko.observable();
	self.minDate = ko.observable();
	self.maxDate = ko.observable();
	self.bookingDate = ko.observable(bookingDate);
	self.formattedBookingDate = ko.pureComputed(function() {
		return self.bookingDate() && util.formatDate(self.bookingDate());
	});
	self.quizEnabled = ko.observable();
	self.passedQuiz = ko.observable();
	self.event = ko.observable();
	self.map = ko.observable();
	self.mapOptions = ko.observable();
	self.fitnessDeclared = ko.observable(false);
	self.confirmationInProgress = ko.observable(false);
	self.bookingError = ko.observable();
	self.googleMapsURL = ko.observable();

	self.bookingConfirmed = ko.observable();
	self.bookingCode = ko.observable();
	self.members = ko.observable();
	self.userEmail = ko.observable();

	self.staticMapURL = ko.observable();
	self.calendarEventURL = ko.pureComputed(function() {
		return birdata.bookingCalendarEventURL(self.bookingCode());
	});

	self.registrationStatus = ko.observable('');
	self.showRegistration = ko.pureComputed(function() {
		return self.registrationStatus() != 'registered';
	});

	self.showQuiz = ko.pureComputed(function() {
		return self.quizEnabled() && !self.passedQuiz();
	});

	self.showBookingForm = ko.pureComputed(function() {
		return !self.showQuiz() && !self.showRegistration();
	});

	self.isJoiningGroupBooking = !!existingBookingCode;
	self.alreadyJoined = ko.observable(false);

	self.showBookingDateInDetails = ko.pureComputed(function() {
		return self.bookingConfirmed() || self.isJoiningGroupBooking;
	});

	self.isDateUnavailable = function(date) {
		if(typeof date != 'string') {
			date = util.formatISODate(date);
		}
		if(date < today) return true;
		if(self.minDate() && date < self.minDate()) return true;
		if(self.maxDate() && date > self.maxDate()) return true;

		if(self.siteAvailability().has(siteID + ":*") || self.siteAvailability().has(siteID + ":" + date)) {
			return true;
		}

		return false;
	}

	self.isSelectedDateInPast = ko.pureComputed(function() {
		var date = self.bookingDate();
		return date && date < today;
	});

	self.isSelectedDateUnavailable = ko.pureComputed(function() {
		return self.bookingDate() && self.isDateUnavailable(self.bookingDate());
	});


	self.eventName = ko.pureComputed(function() {
		return self.event() && self.event().name;
	});

	self.title = ko.pureComputed(function() {
		if(self.bookingConfirmed()) {
			return "Booking Confirmed";
		} else if(self.isJoiningGroupBooking) {
			return "Join Booking";
		} else {
			return "Book Site";
		}
	});

	self.siteAvailability = ko.observable(new Set());

	self.dailyBookingLimit = ko.pureComputed(function() {
		var event = self.event();
		return event && event.max_bookings_per_person_per_day;
	});

	self.haveExceededDailyBookingLimit = ko.pureComputed(function() {
		var event = self.event();
		if(!event || !event.max_bookings_per_person_per_day) {
			return false;
		}
		if(event.participation.is_event_admin) {
			return false;
		}

		var bookingsOnCurrentDate = event.participation.bookings.filter(b => b.date == self.bookingDate());
		if(bookingsOnCurrentDate.length >= event.max_bookings_per_person_per_day) {
			return true;
		}
	});

	self.enableConfirmBooking = ko.pureComputed(function() {
		var badDate = self.isJoiningGroupBooking ?
			self.isSelectedDateInPast() :
			self.isSelectedDateUnavailable();

		return self.fitnessDeclared() &&
			!badDate &&
			!self.haveExceededDailyBookingLimit() &&
			!self.confirmationInProgress();
	});

	// Two different ways to set up depending on whether we are coming from a booking code with no other information,
	// or from the booking map with event, site and date.
	if(existingBookingCode) {
		var bookingPromise = birdata.eventBooking(existingBookingCode);
		Promise.all([
			bookingPromise.then(b => birdata.event(b.event_id)),
			bookingPromise,
			birdata.lookup(),
			programs(),
			birdata.me(),
			Promise.resolve(null),
			bookingPromise.then(b => b.site_id ? birdata.sharedSite(b.site_id) : null)
		]).then(setup).catch(function(error) {
			console.log(error);
			if(error.xhr && error.xhr.status == 404 && error.xhr._meta.url.indexOf("events/bookings/") >= 0) {
				self.error("No booking was found for the code " + existingBookingCode);
			} else {
				self.error("There was a problem loading this booking.");
			}
		});
	} else {
		var eventPromise = birdata.event(eventCode);
		Promise.all([
			eventPromise,
			Promise.resolve(null),
			birdata.lookup(),
			programs(),
			birdata.me(),
			eventPromise.then(e => birdata.eventSiteAvailability(e.id)),
			(+siteID) ? birdata.sharedSite(siteID) : Promise.resolve(null)
		]).then(setup).catch(function(error) {
			console.log(error);
			self.error("There was a problem loading the booking form.");
		})
	}

	function setup([event, booking, lookup, programs, me, siteAvailability, sharedSite]) {
		self.event(event);
		self.program = programs.find(event.program);
		self.userEmail(me.email);

		self.registrationStatus(event.participation.has_registered ? 'registered' : 'unregistered');

		if(siteAvailability) {
			self.siteAvailability(new Set(siteAvailability));
		}

		if(booking) {
			siteID = booking.cell_id || booking.site_id;
			bookingDate = booking.date;
			self.bookingDate(bookingDate);
			self.members(booking.members);
			self.alreadyJoined(booking.members.some(m => m.id == me.id));
		}

		if(self.program.mapGrid) {
			self.siteName(siteID);
		} else {
			self.siteName(sharedSite.name);
		}

		self.minDate(event.start_date);
		self.maxDate(event.finish_date);

		var start = event.start_date;

		self.quizEnabled(!!event.quiz);
		self.passedQuiz(event.participation.passed_quiz);

		self.sharedSite = sharedSite;

		self.loaded(true);
	}

	self.takeQuiz = function() {
		var quiz = self.event().quiz;

		if(quiz.endsWith("_external")) {
			eventUtil.showExternalQuizPopup(self.event(), function() {
				birdata.passedQuiz(quiz).then(self.passedQuiz).catch(e => console.log(e));
			});
		} else {
			var div = h('div', { 'class': 'birdata-embed' }, h('div', { 'class': 'modal', 'data-bind': 'component: { name: "quiz", params: quizParams }' }));
			document.body.appendChild(div);

			ko.applyBindings({
				quizParams: {
					quiz: quiz,
					onCompletion: function() {
						div.parentNode.removeChild(div);
						birdata.passedQuiz(quiz).then(self.passedQuiz).catch(e => console.log(e));
					}
				}
			}, div);
		}
	}

	self.confirmBooking = function() {
		self.bookingError(null);
		self.confirmationInProgress(true);

		var promise;
		if(self.isJoiningGroupBooking) {
			promise = birdata.joinBooking(existingBookingCode);
		} else {
			var bookingData = {
				date: self.bookingDate()
			};
			if(self.program.mapGrid) {
				bookingData.cell_id = siteID;
			} else {
				bookingData.site_id = siteID;
			}
			promise = birdata.createBooking(self.event().id, bookingData);
		}

		promise.then(function(booking) {
			self.bookingCode(booking.booking_code);
			self.bookingConfirmed(true);
		}).catch(function(error) {
			self.bookingError('There was a problem processing your booking. Please try again or contact birdata@birdlife.org.au for assistance.');
			console.log(error);
		}).finally(function() {
			self.confirmationInProgress(false);
		});
	}

	self.emailBookingCode = function() {
		var uri = eventUtil.bookingCodeEmailURI({
			eventName: self.event().name,
			bookingDate: self.bookingDate(),
			siteName: self.siteName(),
			bookingCode: self.bookingCode(),
			googleMapsURL: self.googleMapsURL()
		});
		window.open(uri, '_blank');
	}

	self.register = function() {
		eventUtil.showRegistrationPopup(self.event(), function() {
			self.registrationStatus('updating');
			birdata.registerForEvent(self.event()).then(function() {
				self.registrationStatus('registered');
			}).catch(function() {
				self.registrationStatus('error');
			})
		});
	}

	self.eventPageURL = ko.pureComputed(function() {
		if(self.event()) {
			return '/event?event=' + self.event().code;
		}
	});

	googleMaps.load().then(loadMap);

	function loadMap() {
		if(self.loaded()) {
			if(loadMap.called) {
				return;
			} else {
				loadMap.called = true;
			}
		} else {
			self.loaded.subscribe(function() {
				loadMap();
			});
			return;
		}

		var centroid;
		if(self.program && self.program.mapGrid) {
			centroid = utmGrid.gridCellCentroid(siteID, self.program.mapGrid.size);
		} else if(self.sharedSite) {
			centroid = self.sharedSite.centroid;
		} else {
			return;
		}

		var center = { lat: centroid.lat, lng: centroid.lon };
		var zoom = 14;

		self.googleMapsURL(googleMaps.googleMapsURL(centroid.lat, centroid.lon));

		self.mapOptions(googleMaps.defaultOptions({
			center: center,
			zoom: zoom,
			fullscreenControl: false,
			mapTypeControl: false,
			zoomControl: false,
		}));
	}

	function siteBounds(site, bounds) {
		var sw = new google.maps.LatLng(site.bounds.miny, site.bounds.minx);
		var ne = new google.maps.LatLng(site.bounds.maxy, site.bounds.maxx);
		if(bounds) {
			return bounds.extend(sw).extend(ne);
		} else {
			return new google.maps.LatLngBounds(sw, ne);
		}
	}

	function setupMap(map) {
		if(setupMap.called) {
			return;
		} else {
			setupMap.called = true;
		}

		map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(googleMaps.zoomControl(map));

		if(self.program.mapGrid) {
			var gridSize = self.program.mapGrid.size;

			var centroid = utmGrid.gridCellCentroid(siteID, gridSize);
			var corners = utmGrid.gridCellCorners(siteID, gridSize);
			var path = corners.map(p => ({ lat: p.lat, lng: p.lon }));
			new google.maps.Polygon({
				paths: path,
				fillColor: 'forestgreen',
				strokeColor: 'forestgreen',
				fillOpacity: 0.3,
				strokeWeight: 2,
				map: map
			});
		} else if(self.sharedSite) {
			var site = self.sharedSite;
			map.fitBounds(new google.maps.LatLngBounds(
				new google.maps.LatLng(site.bounds.miny, site.bounds.minx),
				new google.maps.LatLng(site.bounds.maxy, site.bounds.maxx)));
			googleMaps.addFeatureToMap(site.geom, map);
		}
	}

	ko.computed(function() {
		if(self.map() && self.loaded()) {
			setupMap(self.map());
		}
	});

};

ko.components.register('book-site', {
	viewModel: function(params) {
		return new BookSiteViewModel(params);
	},
	template: require('../../templates/book-site.html')
});

export default BookSiteViewModel;
